import { TFunction } from 'i18next'
import moment, { Moment } from 'moment'
import { Tooltip } from 'primereact/tooltip'
import { Nullable } from 'primereact/ts-helpers'
import React from 'react'

import { hexToRgba } from '..'

export type TCalendarDateTemplateInfo = {
  tooltip: string
  startsAt: Moment
  endsAt: Moment
  color: string
  isGeneralAbsence: boolean
  uuid: string
  personUuid: string
}

type TCalendarDateTemplateReturn = {
  day: number
  month: number
  year: number
  today: boolean
  selectable: boolean
}

export const DateTemplate = (
  templateDate: TCalendarDateTemplateReturn,
  absenceInfo: TCalendarDateTemplateInfo[],
  t: TFunction,
  excludeCurrentSelection?: Nullable<(Date | null)[]>,
  excludeEditSelection?: Nullable<(Date | null)[]>,
) => {
  type SelectionCheck = {
    isSelected: boolean
    isStart: boolean
    isEnd: boolean
  }
  const selectionCheck = (): SelectionCheck => {
    const templateDateMoment = `${templateDate.year}/${
      templateDate.month + 1
    }/${templateDate.day}`

    if (excludeCurrentSelection) {
      const excludeStart = moment(excludeCurrentSelection[0]).format('YYYY/M/D')
      const excludeEnd = moment(excludeCurrentSelection[1]).format('YYYY/M/D')
      if (excludeStart === templateDateMoment) {
        return { isSelected: true, isStart: true, isEnd: false }
      } else if (excludeEnd === templateDateMoment) {
        return { isSelected: true, isStart: false, isEnd: true }
      } else if (
        moment(templateDate).isBetween(excludeStart, excludeEnd, 'day', '[]')
      ) {
        return { isSelected: true, isStart: false, isEnd: false }
      }
    } else if (excludeEditSelection) {
      const excludeStart = moment(excludeEditSelection[0]).format('YYYY/M/D')
      const excludeEnd = moment(excludeEditSelection[1]).format('YYYY/M/D')
      if (excludeStart === templateDateMoment) {
        return { isSelected: true, isStart: true, isEnd: false }
      } else if (excludeEnd === templateDateMoment) {
        return { isSelected: true, isStart: false, isEnd: true }
      } else if (
        moment(templateDate).isBetween(excludeStart, excludeEnd, 'day', '[]')
      ) {
        return { isSelected: true, isStart: false, isEnd: false }
      }
    }
    return { isSelected: false, isStart: false, isEnd: false }
  }

  const selectedDate = selectionCheck()

  if (Object.values(selectedDate).some(value => value === true)) {
    const generalStyle = { padding: '1rem', margin: '0' }
    const edgeStyle = {
      ...generalStyle,
      background: '#428be3',
      color: 'white',
    }
    if (selectedDate.isStart || selectedDate.isEnd) {
      return <p style={edgeStyle}>{templateDate.day}</p>
    }
    return <p style={generalStyle}>{templateDate.day}</p>
  }

  if (!absenceInfo || absenceInfo.length === 0) {
    return <>{templateDate.day}</>
  }

  const betweenCheck = () => {
    const templateDateMoment = `${templateDate.year}/${
      templateDate.month + 1
    }/${templateDate.day}`

    const getExclusionInfo = (selection: Nullable<(Date | null)[]>) => {
      const excludeStart = selection
        ? moment(selection[0]).format('YYYY/M/D')
        : null
      const excludeEnd =
        selection && selection[1]
          ? moment(selection[1]).format('YYYY/M/D')
          : excludeStart
      const isExcluded = excludeStart
        ? moment(templateDateMoment).isBetween(
            excludeStart,
            excludeEnd,
            'day',
            '[]',
          )
        : false

      return isExcluded
    }

    const infoReturn = absenceInfo.find(absence => {
      const startsAt = absence.startsAt.clone().format('YYYY/M/D')
      const endsAt = absence.endsAt.clone().format('YYYY/M/D')

      const isExcludedCurrent = getExclusionInfo(excludeCurrentSelection)
      const isExcludedEdit = getExclusionInfo(excludeEditSelection)

      if (isExcludedEdit) return false
      if (isExcludedCurrent) return false

      return moment(templateDateMoment).isBetween(startsAt, endsAt, 'day', '[]')
    })
    return infoReturn
  }

  const foundAbsenceInfo = betweenCheck()
  if (foundAbsenceInfo) {
    const tooltipClass = `custom-tooltip-${foundAbsenceInfo.uuid.split('-')[0]}`
    const tooltipRgba1 = hexToRgba(foundAbsenceInfo.color, 0.3)
    const tooltipRgba2 = hexToRgba(foundAbsenceInfo.color, 0.9)

    return (
      <div>
        <Tooltip mouseTrack className="p-1" target={`.${tooltipClass}`}>
          <p>{t(foundAbsenceInfo.tooltip) as string}</p>
        </Tooltip>
        <strong
          className={tooltipClass}
          style={{
            padding: '1rem',
            background: foundAbsenceInfo.isGeneralAbsence
              ? `repeating-linear-gradient( -45deg, ${tooltipRgba1} 0px, ${tooltipRgba1} 5px, ${tooltipRgba2} 6px, ${tooltipRgba2} 11px, ${tooltipRgba1} 12px)`
              : foundAbsenceInfo.color,
          }}>
          {templateDate.day}
        </strong>
      </div>
    )
  }

  return <>{templateDate.day}</>
}
